import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { REPORTING_PERIODS, REPORTING_PERIOD_LABELS, reportingPeriodRange, toEnglishDigits, } from '@bookora/shared';
import generatePicker from 'antd/es/date-picker/generatePicker';
import faIR from 'antd/es/date-picker/locale/fa_IR';
import dayjs from 'dayjs';
import { Icon } from '../icons/icon.js';
import { JALALI_FORMATS, jalaliGenerateConfig, parseJalaliInput, } from './jalali-generate-config.js';
import { endOfTehranDay, startOfTehranDay } from './tehran-day.js';
/**
 * The Jalali DatePicker family. Same API surface as AntD's, same props, same
 * `Dayjs` values — only the calendar it paints and parses is different.
 *
 * Both panels import these instead of `antd`'s `DatePicker`. An ESLint rule in
 * each app forbids importing `DatePicker` from `antd` directly, because a
 * single Gregorian picker in a Persian form is the kind of thing that ships.
 */
const GeneratedPicker = generatePicker(jalaliGenerateConfig);
/**
 * AntD's Persian strings with the calendar-specific formats corrected: the
 * stock locale carries `M/D/YYYY`, which is the American Gregorian order and
 * reads as nonsense once the numbers are Jalali.
 *
 * The `field…Format` keys are what the INPUT shows, and they are a separate
 * set from `dateFormat`: `@rc-component/picker` fills any that are missing
 * with `YYYY-MM-DD` and `YYYY-MM-DD HH:mm:ss`. So every picker that did not
 * pass its own `format` painted «۱۴۰۵-۰۷-۰۴ ۱۶:۴۲:۲۰» — hyphens and seconds —
 * beside the one that did, «۱۴۰۵/۰۷/۰۴ ۱۶:۳۳». Setting them here makes the
 * slash form and minute precision the default for every call site; the
 * date-time format carrying no `ss` is also what turns the seconds column off
 * in the time panel, since the picker infers its columns from the format.
 */
export const jalaliPickerLocale = {
    ...faIR,
    lang: {
        ...faIR.lang,
        fieldDateFormat: JALALI_FORMATS.date,
        fieldDateTimeFormat: JALALI_FORMATS.dateTime,
        fieldTimeFormat: JALALI_FORMATS.time,
        fieldMonthFormat: 'YYYY/MM',
        dateFormat: JALALI_FORMATS.date,
        dateTimeFormat: JALALI_FORMATS.dateTime,
        yearFormat: JALALI_FORMATS.year,
        monthFormat: 'MMMM',
        placeholder: 'انتخاب تاریخ',
        rangePlaceholder: ['از تاریخ', 'تا تاریخ'],
    },
};
/**
 * ── WHY EACH PICKER IS WRAPPED RATHER THAN RE-EXPORTED ──────────────────────
 *
 * `locale` is defaulted here, and that is not a convenience.
 *
 * `generatePicker` reads its locale from `ConfigProvider` — but it reads it
 * through `antd/es/date-picker/…`, a DEEP import, while `BookoraThemeProvider`
 * mounts the provider from the package ROOT. Whenever a bundler treats those
 * two specifiers as separate modules — which Vitest's dependency optimiser
 * does, and which any `optimizeDeps.include`/`ssr.noExternal` split can do —
 * they get two different `ConfigContext` objects, and the picker silently falls
 * back to AntD's defaults: English placeholders («Start date»), the default
 * theme, and no `-rtl`.
 *
 * `test/antd-rtl.test.tsx` asserts that identity mismatch directly, so this is
 * recorded as a fact about the build rather than a guess.
 *
 * The panels were papering over it by passing `locale={jalaliPickerLocale}` at
 * every call site — which worked, and which meant the one screen that forgot
 * would have shipped an English date field into a Persian clinic form. The
 * default now lives here, and a caller may still override it.
 */
function withJalaliLocale(Picker, displayName) {
    function Wrapped(props) {
        return (_jsx(Picker, { ...props, locale: props.locale ?? jalaliPickerLocale, suffixIcon: props.suffixIcon ?? _jsx(Icon, { name: "calendar", size: 16 }) }));
    }
    Wrapped.displayName = displayName;
    return Wrapped;
}
/**
 * ── THE RANGE SEPARATOR IS A DIRECTIONAL ARROW ──────────────────────────────
 *
 * AntD draws it with `SwapRightOutlined`, which points RIGHT in every
 * direction. A Jalali range reads «از تاریخ ← تا تاریخ» right-to-left, so the
 * stock arrow points from the END of the range back to its start — backwards,
 * on every reporting screen in the console, and invisible unless you look for
 * it.
 *
 * `arrow-forward` is the registry's logical name for "onward in reading
 * order", so it resolves to the left-pointing glyph here and the right-pointing
 * one if this ever renders LTR.
 */
function withRangeSeparator(Picker, displayName) {
    function Wrapped(props) {
        return (_jsx(Picker, { ...props, separator: props.separator ?? _jsx(Icon, { name: "arrow-forward", size: 16, label: "\u062A\u0627" }) }));
    }
    Wrapped.displayName = displayName;
    return Wrapped;
}
/**
 * The format typed entry is read against, or `null` when the wrapper should
 * stand aside: a function or mask format, a multi-date picker, or a
 * week/month/year picker, none of which a receptionist types a day into.
 */
function typedEntryFormat(props) {
    if (props.multiple)
        return null;
    if (props.picker !== undefined && props.picker !== 'date')
        return null;
    const format = Array.isArray(props.format) ? props.format[0] : props.format;
    if (typeof format === 'string')
        return format;
    if (format !== undefined)
        return null;
    return props.showTime ? null : JALALI_FORMATS.date;
}
/**
 * ── A TYPED DATE LANDS BEFORE ANYTHING ELSE HAPPENS ─────────────────────────
 *
 * Two things went wrong between a receptionist typing a birth date and the
 * form saving it, both on the single-date picker:
 *
 *   • rc-picker only accepts text that parses while it is being typed, and
 *     with padded tokens now strict (see `NUMERIC_TOKENS`) «۱۳۷۰/۵/۲۰» never
 *     does — on blur the picker would quietly put the old value back.
 *
 *   • Enter in the field is also Enter in the `<form>`. The browser submitted
 *     the form with whatever the FORM held, which was not yet the typed date:
 *     a patient file was created with «تاریخ تولد —» while the field still
 *     showed a date.
 *
 * So on blur and on Enter, text that differs from the committed value is read
 * once more with the lenient parser and, if it is a date the picker would
 * allow, handed to `onChange` directly. On Enter the key's default is
 * prevented whenever something was pending — the form never submits past a
 * value that has not landed, and a half-typed or impossible date stays on
 * screen, marked invalid, instead of being submitted as «no date». Once the
 * field is settled, Enter submits the form as it does from any other field.
 *
 * Only the single-date picker is wrapped. The range, week, month and year
 * pickers still get the strict parser, which is what stops them repainting a
 * half-typed value; they just do not get the lenient second reading.
 */
function withTypedEntry(Picker, displayName) {
    function Wrapped(props) {
        const entry = props;
        const format = typedEntryFormat(entry);
        if (format === null)
            return _jsx(Picker, { ...props });
        /**
         * `undefined`: nothing pending — the text is what is committed, or empty
         * (clearing is rc-picker's own business). `null`: pending, not a date.
         */
        const pendingValue = (target) => {
            if (!(target instanceof HTMLInputElement))
                return undefined;
            const text = toEnglishDigits(target.value.trim());
            if (text === '')
                return undefined;
            const committed = entry.value?.isValid()
                ? toEnglishDigits(jalaliGenerateConfig.locale.format('fa_IR', entry.value, format))
                : '';
            if (text === committed)
                return undefined;
            const parsed = parseJalaliInput(text, format, { lenient: true });
            if (!parsed)
                return null;
            if (entry.disabledDate?.(parsed, { type: 'date' }))
                return null;
            if (entry.minDate && parsed.isBefore(entry.minDate))
                return null;
            if (entry.maxDate && parsed.isAfter(entry.maxDate))
                return null;
            return parsed;
        };
        const commit = (next) => {
            entry.onChange?.(next, jalaliGenerateConfig.locale.format('fa_IR', next, format));
        };
        const handlers = {
            onKeyDown: (event, preventDefault) => {
                entry.onKeyDown?.(event, preventDefault);
                if (event.key !== 'Enter' || event.defaultPrevented)
                    return;
                const next = pendingValue(event.target);
                if (next === undefined)
                    return;
                event.preventDefault();
                if (next)
                    commit(next);
            },
            onBlur: (event, info) => {
                entry.onBlur?.(event, info);
                const next = pendingValue(event.target);
                if (next)
                    commit(next);
            },
        };
        return _jsx(Picker, { ...props, ...handlers });
    }
    Wrapped.displayName = displayName;
    return Wrapped;
}
export const JalaliDatePicker = withTypedEntry(withJalaliLocale(GeneratedPicker, 'JalaliDatePickerLocale'), 'JalaliDatePicker');
export const JalaliRangePicker = withRangeSeparator(withJalaliLocale(GeneratedPicker.RangePicker, 'JalaliRangePickerLocale'), 'JalaliRangePicker');
export const JalaliMonthPicker = withJalaliLocale(GeneratedPicker.MonthPicker, 'JalaliMonthPicker');
export const JalaliYearPicker = withJalaliLocale(GeneratedPicker.YearPicker, 'JalaliYearPicker');
export const JalaliWeekPicker = withJalaliLocale(GeneratedPicker.WeekPicker, 'JalaliWeekPicker');
export const JalaliTimePicker = GeneratedPicker.TimePicker;
/**
 * The named Jalali windows every reporting screen offers — امروز, این هفته,
 * این ماه, … — as AntD `presets`. Derived from `@bookora/shared`'s
 * `REPORTING_PERIODS` so the panel's "این ماه" and the API's "این ماه" are the
 * same instants, including the Nowruz boundary.
 */
export function useReportingPresets(periods = REPORTING_PERIODS) {
    return useMemo(() => periods.map((period) => {
        const range = reportingPeriodRange(period);
        return {
            label: REPORTING_PERIOD_LABELS[period],
            // The API's ranges are half-open `[start, end)`; a picker shows an
            // inclusive end, so the preset ends one millisecond earlier.
            value: [dayjs(range.start), dayjs(new Date(range.end.getTime() - 1))],
        };
    }), [periods]);
}
/**
 * `[Dayjs, Dayjs]` from a range picker → the instants the API wants: Tehran
 * midnight at the start of the first day, and the last millisecond of the
 * last Tehran day.
 *
 * Tehran days, not `startOf('day')` / `endOf('day')`: those are the BROWSER's
 * midnights, and on a laptop set to another zone the range started and ended
 * half an hour (or a day) away from the days the user picked. See
 * `tehran-day.ts`.
 */
export function toApiRange(value) {
    if (!value)
        return {};
    const [start, end] = value;
    return {
        from: start ? startOfTehranDay(start).toDate().toISOString() : undefined,
        to: end ? endOfTehranDay(end).toDate().toISOString() : undefined,
    };
}
//# sourceMappingURL=jalali-picker.js.map